// src/pages/Terms.jsx
import React from 'react';

export default function Terms() {
  return (
    <main className="page">
      <div className="page-content">
        <div className="card">
          <h1 className="card-title">Terms of Service</h1>
          <p className="card-subtitle">Last Updated: 11/19/2025</p>

          <section className="card-section">
            <p>
              These Terms of Service (&quot;Terms&quot;) govern your use of the Ban Appeals Discord bot and the Ban
              Appeals website (banappeals.org) (collectively, the &quot;Service&quot;). By installing or using the bot,
              accessing the website, or otherwise using the Service, you agree to be bound by these Terms.
            </p>
            <p>
              If you do not agree to these Terms, you must not use the Service.
            </p>
          </section>

          <section className="card-section">
            <h2>1. Eligibility</h2>
            <ul>
              <li>You must comply with Discord&apos;s Terms of Service and Community Guidelines to use the Service.</li>
              <li>
                If you are using the Service on behalf of a server or organization, you represent that you have the
                authority to accept these Terms on its behalf.
              </li>
              <li>
                The Service is not intended for children under the age of 13. If you are under 13, you may not use the
                Service.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>2. Description of the Service</h2>
            <p>
              Ban Appeals provides a Discord bot and website that help Discord servers manage ban appeals and related
              moderation workflows. Features may include:
            </p>
            <ul>
              <li>Appeal submission and tracking tools for users.</li>
              <li>Configuration and management panels for server staff.</li>
              <li>Optional premium features that enhance or extend the core functionality.</li>
            </ul>
            <p>
              We may add, modify, or remove features from the Service at any time, with or without notice.
            </p>
          </section>

          <section className="card-section">
            <h2>3. Use of the Service</h2>
            <ul>
              <li>
                You are responsible for how the Service is configured and used within your Discord server, including
                appeal rules, decisions, and moderation actions.
              </li>
              <li>
                The Service is a tool to assist server staff and does not make moderation decisions on its own; ultimate
                responsibility for moderation outcomes lies with your server&apos;s staff.
              </li>
              <li>
                You agree not to misuse the Service or attempt to interfere with its normal operation or security.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>4. Accounts and Authentication</h2>
            <p>
              Access to certain features of the website may require logging in via Discord OAuth. By doing so, you
              authorize us to access limited information from your Discord account (such as user ID, username, avatar,
              and server memberships) in accordance with our Privacy Policy.
            </p>
            <ul>
              <li>
                You are responsible for maintaining the security of your Discord account and any actions taken while
                authenticated.
              </li>
              <li>
                You must immediately notify us if you believe there has been unauthorized access to your account in
                connection with the Service.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>5. Server Owners and Administrators</h2>
            <p>
              If you are a server owner or administrator installing or managing the bot in your server, you agree that:
            </p>
            <ul>
              <li>You are responsible for complying with all applicable laws and Discord policies.</li>
              <li>
                You are responsible for how appeals are handled, including staff behavior, decisions, and communications
                with users.
              </li>
              <li>
                You will configure and use the Service in a way that is transparent and fair to your server members, as
                applicable to your community.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>6. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable law, regulation, or Discord policy.</li>
              <li>Harass, threaten, or abuse other users or communities.</li>
              <li>
                Attempt to gain unauthorized access to our systems, the data of other users, or any part of the Service
                not intended for you.
              </li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the Service.</li>
              <li>Use automated tools to overload, disrupt, or interfere with the Service.</li>
            </ul>
          </section>

          <section className="card-section">
            <h2>7. Premium Features and Payments</h2>
            <p>
              We may offer optional premium features or subscriptions (&quot;Premium&quot;). Details about Premium,
              including pricing and included features, will be provided separately on the website or within the Service.
            </p>
            <ul>
              <li>
                By subscribing to Premium, you agree to pay all applicable fees and taxes associated with your
                subscription.
              </li>
              <li>
                Fees are generally non-refundable unless explicitly stated otherwise or required by applicable law.
              </li>
              <li>
                We reserve the right to modify Premium features and pricing, with reasonable notice where required.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>8. Intellectual Property</h2>
            <ul>
              <li>
                The Service, including all software, code, design, and branding, is owned by Ban Appeals or its
                licensors and is protected by intellectual property laws.
              </li>
              <li>
                You are granted a limited, non-exclusive, revocable license to use the Service solely for its intended
                purpose within Discord and via the website.
              </li>
              <li>
                You may not copy, modify, distribute, or create derivative works of the Service without our prior
                written permission.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>9. User Content</h2>
            <p>
              &quot;User Content&quot; includes appeals, configurations, and any other data submitted or generated by
              users when using the Service.
            </p>
            <ul>
              <li>
                You retain ownership of your User Content, but grant us a limited license to store, process, and display
                it as necessary to operate the Service.
              </li>
              <li>
                You are responsible for ensuring that your User Content does not violate the rights of others or any
                applicable law.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>10. Disclaimers</h2>
            <p>
              The Service is provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis, without warranties of
              any kind, whether express or implied, including but not limited to implied warranties of merchantability,
              fitness for a particular purpose, or non-infringement.
            </p>
            <p>
              We do not guarantee uninterrupted or error-free operation of the Service, nor do we guarantee that appeals
              or moderation outcomes will be accurate, fair, or satisfactory to all parties.
            </p>
          </section>

          <section className="card-section">
            <h2>11. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Ban Appeals and its owners, developers, and affiliates shall not
              be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data,
              profits, or revenue, arising out of or in connection with your use of the Service.
            </p>
            <p>
              Our total aggregate liability for any claims arising from or related to the Service will not exceed the
              amount you have paid to us for the Service in the last three (3) months, if any.
            </p>
          </section>

          <section className="card-section">
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Ban Appeals and its owners, developers, and affiliates from and
              against any claims, liabilities, damages, losses, and expenses (including reasonable attorney&apos;s fees)
              arising out of or in any way related to:
            </p>
            <ul>
              <li>Your use or misuse of the Service.</li>
              <li>Your violation of these Terms.</li>
              <li>Your violation of any applicable law or the rights of any third party.</li>
            </ul>
          </section>

          <section className="card-section">
            <h2>13. Termination</h2>
            <ul>
              <li>
                We may suspend or terminate your access to the Service at any time, with or without notice, if we
                believe you have violated these Terms or pose a risk to the Service or its users.
              </li>
              <li>
                You may stop using the Service at any time by removing the bot from your server and ceasing to access
                the website.
              </li>
              <li>
                Certain provisions of these Terms will survive termination, including those related to intellectual
                property, disclaimers, limitations of liability, and indemnification.
              </li>
            </ul>
          </section>

          <section className="card-section">
            <h2>14. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will update the &quot;Last Updated&quot; date
              at the top of this page. Your continued use of the Service after changes to the Terms are posted will
              constitute your acceptance of the updated Terms.
            </p>
          </section>

          <section className="card-section">
            <h2>15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States of America,
              without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="card-section">
            <h2>16. Contact</h2>
            <p>If you have any questions about these Terms, you can contact us via:</p>
            <ul>
              <li>Support Server: <a href="/support" target="_blank" rel="noopener noreferrer">Click to join</a></li>
              <li>Email: <a href="mailto:support@banappeals.org" target="_blank" rel="noopener noreferrer">support@banappeals.org</a></li>
            </ul>
          </section>
        </div>
      </div>
    </main>
  );
}
